import * as React from 'react'
import * as sentry from '../../src/utils/sentry'
import * as ImgCache from 'imgcache.js'
import { always } from '../../../shared/lib/functional/function.no-deps'
import { Base_Component } from '../Base/Base.ui.Component'
import { IDB_cachedImages } from '../IDB/IDB.deviceDB'
import { Imag_cache } from './Imag.cache'
import { raceWithTimeout } from '../../../shared/lib/race.no-deps'
import type { WithCachedImageUrl } from '../../../app-core/lib/image/WithCachedImage'
import { withObjectUrl } from '../../../app-core/lib/image/WithObjectUrl'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import { Imag_isImageUrlPointingToLocalContent } from '../Imag/Imag.getUrls'

type Render = (imageUrl: string | null) => React.ReactElement<unknown>

interface Props {
    url: string
    defaultUrl: string
    render: Render
}

interface State {
    url: string | null
    blob?: Blob
}

export const Imag_withCachedImageUrl: WithCachedImageUrl = (
    url: string,
    render: Render,
    defaultUrl = 'res/category_icons/generic.png'
) => <WithCachedImage url={url} defaultUrl={defaultUrl} render={render} />

class WithCachedImage extends Base_Component<Props, State> {
    public state: State = { url: this.props.defaultUrl }
    public componentDidMount = () => this.loadImage()

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.url !== this.props.url) {
            this.loadImage()
        }
    }

    private loadImage = async () => {
        try {
            if (
                this.props.url == null ||
                this.props.url === '' ||
                Imag_isImageUrlPointingToLocalContent(this.props.url)
            ) {
                return
            }

            // Get the cached image from IDB
            const cachedImage = await IDB_cachedImages().get(this.props.url)
            if (cachedImage != null) {
                this.setState({ blob: cachedImage.data })
                return
            }

            // Otherwise look for a legacy cached image
            const legacyCachedUrl = await fetchLegacyCachedUrl(this.props.url)
            if (legacyCachedUrl != null) {
                this.setState({ url: legacyCachedUrl })
            }

            // Otherwise try to cache it
            const blob = await Imag_cache(this.props.url)
            if (blob != null) {
                // And use the now cached image
                this.setState({ blob })
            }
        } catch (error) {
            sentry.err(error, { extra: { url: this.props.url } })
        }
    }

    public tryRender() {
        if (this.state.blob) {
            return withObjectUrl(Base_commonDeps(), this.state.blob, this.props.render)
        }

        return this.props.render(this.state.url)
    }
}

/**
 * @deprecated Images used to be cached with ImgCache.js but they are no longer being added there
 */
function fetchLegacyCachedUrl(remoteUrl: string): Promise<string> {
    // Try to find the locally cached image
    const getCachedFileURL = () =>
        new Promise<string>((resolve, reject) =>
            ImgCache.getCachedFileURL(remoteUrl, (_, localUrl: string) => resolve(localUrl), reject)
        )

    // There are some situations where ImgCache will not call the error callback
    // https://github.com/chrisben/imgcache.js/issues/251
    return raceWithTimeout(100, getCachedFileURL).catch(always(null))
}
